import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  IdTokenClaims,
  InteractionType,
  PopupRequest,
  RedirectRequest,
  SsoSilentRequest,
} from '@azure/msal-browser';
import { Observable, Subscription, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { APP_SETTINGS, AppSettings } from 'src/app.settings';
import { DataLayerService } from './data-layer.service';
import { LocaleService } from './locale.service';
import { AccountService, ProfileService } from './openapi/api/api';
import { AccountInformationResponse, PersonalInformationDto, RegisterRequest } from './openapi/model/models';

export type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string;
  tfp?: string;
};

export type ExtendedIdTokenClaims = IdTokenClaimsWithPolicyId & {
  email: string;
  given_name: string;
  family_name: string;
  preferred_name?: string;
  middle_name?: string;
  tou_consent_choice?: string;
  allowed_notifications?: string; // The claim value is represented in a comma delimiter string of the selected values.
  newUser?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly appSettings: AppSettings = inject(APP_SETTINGS);
  private readonly router: Router = inject(Router);
  private readonly snackBar: MatSnackBar = inject(MatSnackBar);
  private readonly msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private readonly msalService: MsalService = inject(MsalService);
  private readonly accountService: AccountService = inject(AccountService);
  private readonly dataLayerService: DataLayerService = inject(DataLayerService);
  private readonly localeService: LocaleService = inject(LocaleService);
  private readonly profileService: ProfileService = inject(ProfileService);
  private tokenExpiryTimeout: Subscription | null = null;
  private warningTimeout: Subscription | null = null;
  private isLocalIdp = true;

  public get IsLocalIdp(): boolean {
    return this.isLocalIdp;
  }

  public set IsLocalIdp(value: boolean) {
    this.isLocalIdp = value;
  }

  public updateLastLoggedInDate(idtoken: ExtendedIdTokenClaims): void {
    if (!idtoken.newUser) {
      this.accountService.patchLastLogin().subscribe();
    }
  }

  public handleSignUpSignInPolicy(payload: AuthenticationResult, idtoken: IdTokenClaimsWithPolicyId): void {
    if (
      idtoken.acr?.toLowerCase() !== this.appSettings.B2C.SignUpSignInPolicy.toLowerCase() &&
      idtoken.tfp?.toLowerCase() !== this.appSettings.B2C.SignUpSignInPolicy.toLowerCase()
    ) {
      return;
    }

    this.msalService.instance.setActiveAccount(payload.account);
    this.setSessionTimeout(idtoken);
  }

  public handleEditProfilePolicy(idtoken: ExtendedIdTokenClaims): void {
    if (
      idtoken.acr?.toLowerCase() !== this.appSettings.B2C.EditProfilePolicy.toLowerCase() &&
      idtoken.tfp?.toLowerCase() !== this.appSettings.B2C.EditProfilePolicy.toLowerCase()
    ) {
      return;
    }

    /**
     * For the purpose of setting an active account for UI update, we want to consider only the auth response resulting
     * from SUSI flow. "acr" claim in the id token tells us the policy (NOTE: newer policies may use the "tfp" claim instead).
     * To learn more about B2C tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
     */
    // retrieve the account from initial sing-in to the app
    const originalSignInAccount = this.msalService.instance
      .getAllAccounts()
      .find(
        (account: AccountInfo) =>
          account.idTokenClaims?.oid === idtoken.oid &&
          account.idTokenClaims?.sub === idtoken.sub &&
          ((account.idTokenClaims as IdTokenClaimsWithPolicyId).acr ===
            this.appSettings.B2C.SignUpSignInPolicy.toLowerCase() ||
            (account.idTokenClaims as IdTokenClaimsWithPolicyId).tfp ===
              this.appSettings.B2C.SignUpSignInPolicy.toLowerCase())
      );

    const signUpSignInFlowRequest: SsoSilentRequest = {
      authority: this.appSettings.B2C.SignUpSignInAuthority,
      account: originalSignInAccount,
    };

    // silently login again with the signUpSignIn policy
    this.msalService.ssoSilent(signUpSignInFlowRequest);

    // Update CraftWorker profile with the new information
    const personalInformation: PersonalInformationDto = {
      given_name: idtoken.given_name,
      family_name: idtoken.family_name,
      preferred_name: idtoken.preferred_name,
      middle_name: idtoken.middle_name,
    };
    this.profileService.updatePersonalInformation(this.localeService.regionCode, personalInformation).subscribe();
  }

  public handleChangePasswordPolicy(idtoken: IdTokenClaimsWithPolicyId): void {
    if (
      idtoken.acr?.toLowerCase() !== this.appSettings.B2C.ChangePasswordPolicy.toLowerCase() &&
      idtoken.tfp?.toLowerCase() !== this.appSettings.B2C.ChangePasswordPolicy.toLowerCase()
    ) {
      return;
    }

    this.snackBar.open('Password changed successfully', undefined, { duration: 3000 });

    this.IsLocalIdp = true;
  }

  public handleChangeSignInNamePolicy(idtoken: ExtendedIdTokenClaims): void {
    if (
      idtoken.acr?.toLowerCase() !== this.appSettings.B2C.ChangeSignInNamePolicy.toLowerCase() &&
      idtoken.tfp?.toLowerCase() !== this.appSettings.B2C.ChangeSignInNamePolicy.toLowerCase()
    ) {
      return;
    }

    this.accountService.updateEmailSignInName(idtoken.email).subscribe({
      next: () => {
        this.snackBar.open('Email changed successfully', undefined, { duration: 3000 });
      },
    });
  }

  public handleDeleteAccountPolicy(idtoken: IdTokenClaimsWithPolicyId): void {
    if (
      idtoken.acr?.toLowerCase() !== this.appSettings.B2C.DeleteAccountPolicy.toLowerCase() &&
      idtoken.tfp?.toLowerCase() !== this.appSettings.B2C.DeleteAccountPolicy.toLowerCase()
    ) {
      return;
    }

    this.msalService.logout();
  }

  public handleSocialAccountLinkingPolicy(idtoken: IdTokenClaimsWithPolicyId): void {
    if (
      idtoken.acr?.toLowerCase() !== this.appSettings.B2C.AccountLinkingPolicy.toLowerCase() &&
      idtoken.tfp?.toLowerCase() !== this.appSettings.B2C.AccountLinkingPolicy.toLowerCase()
    ) {
      return;
    }

    this.snackBar.open('Social account linking changed successfully', undefined, { duration: 3000 });
  }

  public handleAccountRetrieval(idtoken: ExtendedIdTokenClaims): void {
    const account$ = idtoken.newUser ? this.registerUser(idtoken) : this.getAccount(idtoken);
    account$.subscribe({
      next: (response) => {
        this.dataLayerService.logUserId(response.craftWorkerId);
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  /**
   * Retrieves the account information for the user with the given idtoken.
   * If the account does not exist (i.e., a 404 error is returned), it registers a new user.
   * This is done to handle the case where the user was already signed up in the B2C tenant, but not through CraftLink.
   *
   * @param idtoken - The idtoken containing user information.
   * @returns An Observable of the account information response.
   */
  private getAccount(idtoken: ExtendedIdTokenClaims): Observable<AccountInformationResponse> {
    return this.accountService.getAccount().pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return this.registerUser(idtoken);
        } else {
          throw error;
        }
      })
    );
  }

  /**
   * Registers a new user with the given idtoken information.
   * After successful registration, it retrieves the account information.
   *
   * @param idtoken - The idtoken containing user information.
   * @returns An Observable of the account information response.
   */
  private registerUser(idtoken: ExtendedIdTokenClaims): Observable<AccountInformationResponse> {
    const allowedNotifications: string[] = idtoken.allowed_notifications?.split(',') ?? [];

    const registerRequest: RegisterRequest = {
      given_name: idtoken.given_name,
      family_name: idtoken.family_name,
      preferred_name: idtoken.preferred_name,
      middle_name: idtoken.middle_name,
      hasAgreedToTermsAndConditions: true, // TODO Only call register is newUser claim is present and true
      // hasAgreedToTermsAndConditions: idtoken.tou_consent_choice === 'AgreeToTermsOfUseConsentYes',
      allowDistrictNewsNotification: allowedNotifications.includes('PclNews'),
      allowEmploymentOpportunitiesNotification: allowedNotifications.includes('FutureEmploymentOpportunities'),
      allowUpcomingEventsNotification: allowedNotifications.includes('UpcomingPclEvents'),
    };

    return this.accountService.registerAccount(registerRequest).pipe(switchMap(() => this.accountService.getAccount()));
  }

  private setSessionTimeout(idToken: IdTokenClaims): void {
    if (this.warningTimeout) {
      this.warningTimeout.unsubscribe();
    }
    if (this.tokenExpiryTimeout) {
      this.tokenExpiryTimeout.unsubscribe();
    }

    // Use token expiration time, or one hour from now if no expiration time claim is present
    const tokenExpiryDate = idToken.exp ? new Date(idToken.exp * 1000) : new Date(Date.now() + 60 * 60 * 1000);
    const timeoutDuration = tokenExpiryDate.getTime() - Date.now();
    const warningDuration = timeoutDuration - 5 * 60 * 1000; // 5 minutes before timeout

    this.warningTimeout = timer(warningDuration).subscribe(() => {
      this.snackBar
        .open('Your session will expire soon. Click here to extend it.', 'Extend', { duration: 5 * 60 * 1000 })
        .onAction()
        .subscribe(() => {
          this.renewToken();
        });
    });

    this.tokenExpiryTimeout = timer(timeoutDuration).subscribe(() => {
      this.msalService.logout();
      this.router.navigate(['/']);
    });
  }

  private renewToken(): void {
    const account = this.msalService.instance.getActiveAccount();

    if (!account) {
      return;
    }

    const signUpSignInFlowRequest: SsoSilentRequest = {
      authority: this.appSettings.B2C.SignUpSignInAuthority,
      account: account,
    };
    this.msalService.ssoSilent(signUpSignInFlowRequest).subscribe({
      next: (response: AuthenticationResult) => {
        this.setSessionTimeout(response.idTokenClaims);
      },
    });
  }

  editProfile() {
    const editProfileFlowRequest: RedirectRequest | PopupRequest = {
      authority: this.appSettings.B2C.EditProfileAuthority,
      scopes: [],
    };

    this.executeFlowRequest(editProfileFlowRequest);
  }

  linkAccount() {
    const accountLinkingFlowRequest: RedirectRequest | PopupRequest = {
      authority: this.appSettings.B2C.AccountLinkingAuthority,
      scopes: [],
    };

    this.executeFlowRequest(accountLinkingFlowRequest);
  }

  deleteAccount() {
    const deleteProfileFlowRequest: RedirectRequest | PopupRequest = {
      authority: this.appSettings.B2C.DeleteAccountAuthority,
      scopes: [],
    };

    this.executeFlowRequest(deleteProfileFlowRequest);
  }

  changeSignInName() {
    const changeSignInNameFlowRequest: RedirectRequest | PopupRequest = {
      authority: this.appSettings.B2C.ChangeSignInNameAuthority,
      scopes: [],
    };

    this.executeFlowRequest(changeSignInNameFlowRequest);
  }

  changePassword() {
    const changePasswordFlowRequest: RedirectRequest | PopupRequest = {
      authority: this.appSettings.B2C.ChangePasswordAuthority,
      scopes: [],
    };

    this.executeFlowRequest(changePasswordFlowRequest);
  }

  private executeFlowRequest(userFlowRequest?: RedirectRequest | PopupRequest) {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this.msalGuardConfig.authRequest) {
        this.msalService
          .loginPopup({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as PopupRequest)
          .subscribe((response: AuthenticationResult) => {
            this.msalService.instance.setActiveAccount(response.account);
          });
      } else {
        this.msalService.loginPopup(userFlowRequest).subscribe((response: AuthenticationResult) => {
          this.msalService.instance.setActiveAccount(response.account);
        });
      }
    } else {
      if (this.msalGuardConfig.authRequest) {
        this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as RedirectRequest);
      } else {
        this.msalService.loginRedirect(userFlowRequest);
      }
    }
  }
}
